import {
  isIncomingMessageListItem,
  isInitialMessageListItem,
  isSentMessageListItem,
  type MessengerLastMessage,
  type MessengerMessageListItem,
} from '@widgets/messenger/types/chat/message'
import { defineStore } from '@widgets/shared/utils/defineStore'
import { mergeWith, sortBy, uniqBy } from 'lodash-es'
import {
  isChatIdleState,
  type MessengerChatState,
  type MessengerMessageListState,
} from '@widgets/messenger/types/chat/chat'
import type { ChatScrollPosition } from '@widgets/shared/types'
import { useChatLocalStorage } from '@widgets/messenger/compositions/useChatLocalStorage'
import { computed, ref } from '@/composition/vue/compositionApi'
import type { DeepPartial } from '@/types'

export const chatStoreSymbol = Symbol('chatStore')

export const useChatStore = defineStore(chatStoreSymbol, () => {
  const chatLocalStorage = useChatLocalStorage()

  // Refs

  const isConnected = ref(true)
  const chatToken = ref<string>('')
  const chatState = ref<MessengerChatState>({
    type: 'idle',
  })
  const userEmail = ref<string>('')
  const messageListItems = ref<MessengerMessageListItem[]>([])
  const messageListState = ref<MessengerMessageListState>({
    isAllMessagesLoaded: false,
    isNextPageLoading: false,
  })
  const agentTypings = ref<Record<string, boolean>>({})
  const currentScrollPosition = ref<ChatScrollPosition>({
    scrollTop: 0,
    atBottom: true,
  })
  const isNewMessagesInsideView = ref(false)

  // Computed

  const isActiveChat = computed(() => !isChatIdleState(chatState.value))
  const agentTypingCount = computed(() => Object.values(agentTypings.value).filter((v) => v).length)
  const lastScrollPosition = computed(() => chatLocalStorage.value.value.lastScrollPosition)
  const newMessageState = computed(() => chatLocalStorage.value.value.newMessageState)
  const hasNotViewedNewMessages = computed(() => {
    return newMessageState.value && !newMessageState.value.isViewed
  })
  const numberOfNewMessages = computed(() => {
    if (!newMessageState.value) {
      return 0
    }
    const incomingMessages = messageListItems.value.filter((item) => isIncomingMessageListItem(item))
    const firstNewMessageIndex = incomingMessages.findIndex(
      (item) => item.id === newMessageState.value?.firstNewMessageId,
    )

    return incomingMessages.length - firstNewMessageIndex
  })
  const lastMessage = computed<MessengerLastMessage | undefined>(() => {
    return messageListItems.value.findLast((item) => {
      return isIncomingMessageListItem(item) || isSentMessageListItem(item) || isInitialMessageListItem(item)
    })
  })

  // Methods

  const resetStore = () => {
    isConnected.value = true
    chatToken.value = ''
    chatState.value = {
      type: 'idle',
    }
    userEmail.value = ''
    messageListItems.value = []
    messageListState.value = {
      isAllMessagesLoaded: false,
      isNextPageLoading: false,
    }
    agentTypings.value = {}
    currentScrollPosition.value = {
      scrollTop: 0,
      atBottom: true,
    }
    isNewMessagesInsideView.value = false
  }

  const setIsConnected = (value) => {
    isConnected.value = value
  }

  const setChatToken = (token: string) => {
    chatToken.value = token
  }

  const setChatState = (state: MessengerChatState) => {
    chatState.value = state
  }

  const patchMessageListState = (value: Partial<MessengerMessageListState>) => {
    messageListState.value = {
      ...messageListState.value,
      ...value,
    }
  }

  const setUserEmail = (email: string) => {
    userEmail.value = email
  }

  const setAgentTyping = (agentId: string, isTyping: boolean) => {
    agentTypings.value[agentId] = isTyping
  }

  const findMessageListItem = (id: number | string) => {
    return messageListItems.value.find((item) => item.id === id)
  }

  const addMessageListItem = (item: MessengerMessageListItem) => {
    messageListItems.value.push(item)
  }

  const patchMessageListItem = <T extends MessengerMessageListItem>(id: number | string, item: DeepPartial<T>) => {
    const index = messageListItems.value.findIndex((i) => i.id === id)
    if (index === -1) {
      return
    }
    const oldItem = messageListItems.value[index] as T
    const newItem = mergeWith(oldItem, item, (_objValue, srcValue) => {
      // Don't merge arrays, we need replace it
      return Array.isArray(srcValue) ? srcValue : undefined
    })
    messageListItems.value.splice(index, 1, newItem)
  }

  const removeMessageListItem = (id: number | string) => {
    removeMessageListItems([id])
  }

  const removeMessageListItems = (ids: Array<number | string>) => {
    const incomingMessageIdsBeforeRemove = messageListItems.value
      .filter((item) => isIncomingMessageListItem(item))
      .map((item) => item.id)

    messageListItems.value = messageListItems.value.filter((item) => !ids.includes(item.id))

    const newMessageId = newMessageState.value?.firstNewMessageId
    if (!newMessageId || !ids.includes(newMessageId)) {
      return
    }
    const newMessageIndex = incomingMessageIdsBeforeRemove.findIndex((id) => id === newMessageId)
    if (newMessageIndex === -1) {
      resetNewMessageState()
    }
    const nextNewMessageId = incomingMessageIdsBeforeRemove.find((id, index) => {
      return index > newMessageIndex && !ids.includes(id)
    })
    if (nextNewMessageId) {
      chatLocalStorage.patch({
        newMessageState: {
          firstNewMessageId: nextNewMessageId,
        },
      })
    } else {
      resetNewMessageState()
    }
  }

  const removeAllMessageListItems = () => {
    messageListItems.value = []
    chatLocalStorage.patch({
      newMessageState: undefined,
    })
  }

  const mergeMessageListItems = (items: MessengerMessageListItem[]) => {
    let result = [...messageListItems.value, ...items]
    result = uniqBy(result, 'id')
    result = sortBy(result, 'id')
    messageListItems.value = result
  }

  const unshiftMessageListItems = (items: MessengerMessageListItem[]) => {
    messageListItems.value.unshift(...items)
  }

  const setCurrentScrollPosition = (position: ChatScrollPosition) => {
    currentScrollPosition.value = position
    chatLocalStorage.patch({
      lastScrollPosition: position,
    })
  }

  const scoreNewMessage = (incomingMessageId: number | string) => {
    if (hasNotViewedNewMessages.value) {
      return
    }
    chatLocalStorage.patch({
      newMessageState: {
        firstNewMessageId: incomingMessageId,
        isViewed: false,
      },
    })
  }

  const setNewMessageStateAsViewed = () => {
    if (!newMessageState.value) {
      return
    }
    chatLocalStorage.patch({
      newMessageState: {
        isViewed: true,
      },
    })
  }

  const resetNewMessageState = () => {
    chatLocalStorage.patch({
      newMessageState: undefined,
    })
  }

  const setIsNewMessagesInsideView = (value: boolean) => {
    isNewMessagesInsideView.value = value
  }

  return {
    isConnected,
    isActiveChat,
    chatToken,
    chatState,
    userEmail,
    messageListItems,
    messageListState,
    agentTypingCount,
    currentScrollPosition,
    lastScrollPosition,
    isNewMessagesInsideView,
    newMessageState,
    hasNotViewedNewMessages,
    numberOfNewMessages,
    lastMessage,
    resetStore,
    setIsConnected,
    setChatToken,
    setChatState,
    setUserEmail,
    setAgentTyping,
    patchMessageListState,
    findMessageListItem,
    addMessageListItem,
    patchMessageListItem,
    removeMessageListItem,
    removeMessageListItems,
    removeAllMessageListItems,
    mergeMessageListItems,
    unshiftMessageListItems,
    setCurrentScrollPosition,
    scoreNewMessage,
    setNewMessageStateAsViewed,
    resetNewMessageState,
    setIsNewMessagesInsideView,
  }
})
